import React from 'react'
import { BusyButton } from '@ztechapps/webui.button'
import { useAutoPopulateProductMetadata, AppSettingKey } from '@ztechapps/product.common'
import { useAppSetting, useToast, useConfirmDialog } from '@ztechapps/common.hook'
import { useMetadataContext } from './Context'

/**
 * Generate Spec button component
 *
 * Automatically populates product metadata (dimensions, shape, category, die set type)
 * by parsing the product code. Only populates missing metadata fields.
 *
 * Visibility controlled by the `PRODUCT_AUTO_GENERATE_NAME_AND_SPEC_BY_AI` app setting.
 */
export const GenerateSpecButton = () => {
  const { _id, onSave, compact } = useMetadataContext()
  const [populateMetadata, { loading }] = useAutoPopulateProductMetadata()
  const toast = useToast()

  // Check if the feature is enabled via app setting
  const { setting } = useAppSetting({ appSettingKey: AppSettingKey.PRODUCT_AUTO_GENERATE_NAME_AND_SPEC_BY_AI })
  const isEnabled = setting?.value === 'true'

  const handlePopulateMetadata = async () => {
    try {
      const product = await populateMetadata({ productId: _id })

      // Refresh the metadata list by calling onSave with the new metadata
      await onSave(product?.metadata)

      toast.showSuccess({ message: 'Specifications populated successfully' })
    } catch (err) {
      toast.showError({ message: err?.message || 'Failed to populate specifications' })
    }
  }

  const { onConfirm } = useConfirmDialog({
    title: 'Generate Specifications?',
    text: 'This will automatically populate missing metadata fields from the product code:\n\n• Vertical (In & mm)\n• Horizontal (In & mm)\n• Category\n• Die Set Type\n• Shape\n\nExisting values will be preserved.',
    buttons: {
      confirm: {
        text: 'Yes, generate!',
      },
    },
    confirmed: handlePopulateMetadata,
  })

  // Hide button if feature is disabled
  if (!isEnabled) {
    return null
  }

  const label = 'Generate Spec'

  return (
    <BusyButton
      text={compact ? '' : label}
      icon="wand-magic-sparkles"
      onClick={onConfirm}
      isBusy={loading}
      busyText={compact ? '' : 'Generating...'}
      aria-label={compact ? label : undefined}
      title={label}
    />
  )
}
